/**
 * LRU cache for Google Place Details with sessionStorage persistence.
 * Deduplicates in-flight requests so multiple markers opening the same place
 * don't trigger parallel API calls.
 */

const PLACE_DETAILS_CACHE_LIMIT = 220;
const SESSION_STORAGE_KEY = 'ff_place_details_cache';
const placeDetailsCache = new Map();
const placeDetailsRequestCache = new Map();
let placeDetailsCacheHydrated = false;

function ensurePlaceDetailsCacheHydrated() {
  if (placeDetailsCacheHydrated) return;
  placeDetailsCacheHydrated = true;

  // Lazy-hydrate cache from sessionStorage so initial map startup is not blocked by JSON parse.
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return;
    const entries = JSON.parse(stored);
    if (!Array.isArray(entries)) return;
    for (const [k, v] of entries) placeDetailsCache.set(k, v);
  } catch (_) { /* ignore */ }
}

function persistToSession() {
  try {
    const entries = [...placeDetailsCache.entries()];
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(entries));
  } catch (_) { /* quota or private mode — ignore */ }
}

export function getCachedPlaceDetails(cacheKey) {
  if (!cacheKey) return null;
  ensurePlaceDetailsCacheHydrated();
  const cached = placeDetailsCache.get(cacheKey);
  if (!cached) return null;
  placeDetailsCache.delete(cacheKey);
  placeDetailsCache.set(cacheKey, cached);
  return cached;
}

export function setCachedPlaceDetails(cacheKey, value) {
  if (!cacheKey || !value) return;
  ensurePlaceDetailsCacheHydrated();
  if (placeDetailsCache.has(cacheKey)) placeDetailsCache.delete(cacheKey);
  placeDetailsCache.set(cacheKey, value);
  if (placeDetailsCache.size > PLACE_DETAILS_CACHE_LIMIT) {
    const oldestKey = placeDetailsCache.keys().next().value;
    if (oldestKey) placeDetailsCache.delete(oldestKey);
  }
  persistToSession();
}

export function loadPlaceDetailsOnce(cacheKey, loader) {
  const cached = getCachedPlaceDetails(cacheKey);
  if (cached) return Promise.resolve(cached);

  const pending = placeDetailsRequestCache.get(cacheKey);
  if (pending) return pending;

  const request = Promise.resolve()
    .then(loader)
    .then((value) => {
      if (value) setCachedPlaceDetails(cacheKey, value);
      return value;
    })
    .finally(() => {
      placeDetailsRequestCache.delete(cacheKey);
    });

  placeDetailsRequestCache.set(cacheKey, request);
  return request;
}
